import BoldSmallText from '@/components/BoldSmallText';
import ContextualBar from '@/components/ContextualBar';
import CustomCard from '@/components/customCard';
import RegularText from '@/components/RegularText';
import Switch from '@/components/Switch';
import UpgradeTextWithTouchPoint from '@/components/UpgradeTextWithTouchPoint';
import { BREAKPOINT, DeliveryMethodLayout, HighlightContentPreviewId, Touchpoint } from '@/constants/enum';
import { dateDisplayMode, dateFormat, optionLayoutDeliveryMethod, optionsSortBy } from '@/constants/options';
import { PATH } from '@/constants/path';
import { checkShowErrorInline, deepObjectEqual } from '@/helpers';
import { stringify } from '@/helpers/string';
import useClickHighlight from '@/hooks/useClickHighlight';
import useConfirmationNavigate from '@/hooks/useConfirmationNavigate';
import { useCustomUserIdClarity } from '@/hooks/useCustomUserIdClarity';
import useToast from '@/hooks/useToast';
import useUserPlan from '@/hooks/useUserPlan';
import Template from '@/pages/Settings/Components/Appearance/components/Template';
import TimelineGraphic from '@/pages/Settings/Components/Appearance/components/TimelineGraphic';
import { apiCaller } from '@/redux/query';
import appbarSlice, { isShowContextualBarSelector } from '@/redux/slice/appbar.slice';
import { isDisabledSelector } from '@/redux/slice/error.slice';
import { highlightPreviewSelector } from '@/redux/slice/highlightPreview.slice';
import { InitDataSettings } from '@/redux/slice/initialState';
import navigateSlice from '@/redux/slice/navigate';
import settingsSlice, { dataSettingsBackupSelector, dataSettingsSelector } from '@/redux/slice/settings.slice';
import { IParamsApi } from '@/types/apis/params';
import {
  Button,
  Collapsible as CollapsibleShopify,
  Divider,
  InlineGrid,
  Link,
  RadioButton,
  Select,
  TextField,
} from '@shopify/polaris';
import { HideIcon, ViewIcon } from '@shopify/polaris-icons';
import { memo, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useMediaQuery } from 'react-responsive';
import CountdownSettings from './components/CountdownSettings';
import CustomCss from './components/CustomCss';
import { AppearanceStyled } from './styled';

interface IProps {
  hideDisplayFormat?: boolean;
  hideLayoutGraphic?: boolean;
}

const Appearance = (props: IProps) => {
  useCustomUserIdClarity('Appearance settings');
  const { show } = useToast();
  const dispatch = useDispatch();
  const { planKey, isPlanLimited, isFreePlan } = useUserPlan();
  const navigate = useConfirmationNavigate();
  const { handleClickHighlight } = useClickHighlight();
  const isMobile = useMediaQuery({ maxWidth: BREAKPOINT.SM });

  const dateAppearanceRef = useRef<HTMLDivElement>(null);
  const zipcodeCheckerInputIndicatorRef = useRef<HTMLDivElement>(null);
  const [updateGeneralSetting, { isLoading }] = apiCaller.useUpdateGeneralSettingMutation();

  const isDisabledSave = useSelector(isDisabledSelector);
  const dataSettings = useSelector(dataSettingsSelector);
  const isShowContextualBar = useSelector(isShowContextualBarSelector);
  const {
    isHighlightedDateAppearance,
    isHighlightedZipcodeCheckerInputIndicatorSetting,
    isHighlightedZipcodeCheckerSubmitButtonSetting,
  } = useSelector(highlightPreviewSelector);

  const { zipcodeSetting } = dataSettings;
  const isDisabled = planKey === 'free';
  const dateFormatOptions = dateFormat(dataSettings.date_locale);

  const scrollIntoView = (ref: React.RefObject<HTMLDivElement>, condition: boolean) => {
    condition && ref.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  useEffect(() => {
    scrollIntoView(dateAppearanceRef, isHighlightedDateAppearance);
  }, [isHighlightedDateAppearance]);

  useEffect(() => {
    scrollIntoView(zipcodeCheckerInputIndicatorRef, isHighlightedZipcodeCheckerInputIndicatorSetting);
  }, [isHighlightedZipcodeCheckerInputIndicatorSetting]);

  const handleChange = (key: keyof typeof dataSettings) => (value: string | number | boolean | IParamsApi.IZipcodeSetting) => {
    dispatch(
      settingsSlice.actions.handleDataSettings({
        ...dataSettings,
        [key]: value,
      }),
    );
  };

  const handleChangeZipcodeSettings = (key: keyof typeof zipcodeSetting) => (value: string | boolean) => {
    const dataZipcode: IParamsApi.IZipcodeSetting = {
      ...zipcodeSetting,
      [key]: value,
    };
    if (dataZipcode) {
      handleChange('zipcodeSetting')(dataZipcode);
    }
  };

  const handleBlurColor = (key: keyof typeof dataSettings) => (e: any) => {
    const { value } = e.target;
    if (dataSettings) {
      dispatch(
        settingsSlice.actions.handleDataSettings({
          ...dataSettings,
          [key]: value,
        }),
      );
    }
  };

  const handleBlurColorZipcodeSettings = (key: keyof typeof zipcodeSetting) => (e: any) => {
    const { value } = e.target;
    if (zipcodeSetting) {
      handleChange('zipcodeSetting')({
        ...zipcodeSetting,
        [key]: value,
      });
    }
  };

  const dataSettingsBackup = useSelector(dataSettingsBackupSelector);
  useEffect(() => {
    const check = !deepObjectEqual(dataSettings, dataSettingsBackup);
    dispatch(appbarSlice.actions.handleUpdateContextualBarState({ isShowContextualBar: check }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stringify(dataSettingsBackup), stringify(dataSettings)]);
  const handleDiscard = () => {
    if (isShowContextualBar && dataSettingsBackup) {
      dispatch(settingsSlice.actions.handleDataSettings(dataSettingsBackup));
    }
  };

  const handleSave = () => {
    if (isShowContextualBar && dataSettings) {
      updateGeneralSetting({
        ...dataSettings,
      }).then((res) => {
        const checkError = checkShowErrorInline(res);
        if (!checkError.status) {
          show('Saved');
          dispatch(settingsSlice.actions.handleDataSettingsBackup(dataSettings));
        }
      });
    }
  };

  const handleNavigateToTranslation = () => {
    if (!deepObjectEqual(dataSettings, dataSettingsBackup)) {
      const { pathname, search } = PATH.TRANSLATION;
      dispatch(
        navigateSlice.actions.handleNavigate({
          pathname,
          search,
          state: { isSetDefaultLanguage: true },
        }),
      );
      return;
    }

    navigate(PATH.TRANSLATION, { state: { isSetDefaultLanguage: true } });
  };
  return (
    <AppearanceStyled>
      <ContextualBar
        isVisible={isShowContextualBar}
        saveAction={handleSave}
        discardAction={handleDiscard}
        loadingSave={isLoading}
        isDisabled={isDisabledSave}
      />
      {!props.hideDisplayFormat ? (
        <CustomCard title="Display mode" textClassName="bold-text-styled">
          <div className="mt-8 d-flex">
            {optionLayoutDeliveryMethod.map((item) => {
              return (
                <div key={item.value} className="mr-16">
                  <RadioButton
                    label={item.label}
                    checked={dataSettings?.choose_layout === item.value}
                    id={item.value}
                    name={item.value}
                    onChange={(_: boolean, newValue: DeliveryMethodLayout) => {
                      handleChange('choose_layout')(newValue);
                    }}
                  />
                </div>
              );
            })}
          </div>
        </CustomCard>
      ) : null}
      {!props.hideLayoutGraphic ? (
        <CustomCard title="Layout graphic" textClassName="bold-text-styled">
          <div className="divider">
            <RegularText>Set up how to show estimated delivery information to customers.</RegularText>
          </div>

          <div className="mt-8 divider">
            <Select
              options={optionsSortBy}
              value={dataSettings.ruleSortBy}
              onChange={handleChange('ruleSortBy')}
              label={'Rule order displayed by'}
            />
          </div>

          <div className="mt-8 divider">
            <InlineGrid columns={2}>
              <BoldSmallText>ETA information</BoldSmallText>
              <div className="content-right">
                <Switch
                  isActive={dataSettings.enable_eta_information}
                  onSwitch={(value: boolean) => {
                    dispatch(
                      settingsSlice.actions.handleDataSettings({
                        ...dataSettings,
                        enable_eta_information: value,
                        show_shipping_method: value ? dataSettings.enable_eta_information : false,
                      }),
                    );
                  }}
                />
              </div>
            </InlineGrid>
          </div>

          {dataSettings.choose_layout === DeliveryMethodLayout.Card ? (
            <div className="mt-8">
              <CollapsibleShopify
                id="show-shipping-method-header"
                open={dataSettings.enable_eta_information}
                transition={{ duration: '500ms' }}
              >
                <InlineGrid columns={['twoThirds', 'oneThird']}>
                  <BoldSmallText>Show banner header</BoldSmallText>
                  <div className="content-right">
                    <Switch
                      isLoading={!dataSettings.enable_eta_information}
                      isActive={dataSettings.show_shipping_method}
                      onSwitch={(value: boolean) => {
                        handleChange('show_shipping_method')(value);
                      }}
                    />
                  </div>
                </InlineGrid>
              </CollapsibleShopify>
              <CollapsibleShopify
                id="shipping-method-header-text"
                open={dataSettings.show_shipping_method}
                transition={{ duration: '500ms' }}
              >
                <div onClick={handleClickHighlight('isHighlightedShippingMethodHeader')}>
                  <TextField
                    id={HighlightContentPreviewId.ShippingMethodHeader}
                    label=""
                    autoComplete="off"
                    value={dataSettings.shippingMethodLabel}
                    onChange={handleChange('shippingMethodLabel')}
                  />
                </div>
              </CollapsibleShopify>
            </div>
          ) : null}

          <div className="mt-8">
            <TimelineGraphic />
          </div>
        </CustomCard>
      ) : null}
      <CustomCard
        title="Visual appearance editor"
        textClassName={`bold-text-styled ${isFreePlan ? 'text--gray' : ''}`}
        titleContentRight={
          isMobile ? undefined : (
            <div>
              <UpgradeTextWithTouchPoint touchPoint={Touchpoint.AppearanceVisualEditor} text="Standard or above" />
            </div>
          )
        }
      >
        <RegularText className={`${isFreePlan ? 'text--gray' : ''}`}>Set up the look and feel of the ETA banner.</RegularText>
        {isMobile && (
          <div>
            <UpgradeTextWithTouchPoint touchPoint={Touchpoint.AppearanceVisualEditor} text="Standard or above" />
          </div>
        )}
        <div className={`mt-8 w-100 ${isDisabled ? 'disable-content' : ''}`}>
          <Template
            handleSelectTemplate={(data: { background_color: string; border_color: string; icon_color: string }) => {
              dispatch(
                settingsSlice.actions.handleDataSettings({
                  ...dataSettings,
                  ...data,
                  text_color: '#000000',
                }),
              );
            }}
          />
          <div className="mt-8">
            <InlineGrid columns={{ xl: 2, lg: 2, md: 2, sm: 2, xs: 1 }} gap={'400'}>
              <TextField
                label={'Text color'}
                suffix={
                  <input
                    type="color"
                    defaultValue={dataSettings.text_color}
                    key={dataSettings.text_color}
                    onBlur={handleBlurColor('text_color')}
                    disabled={isDisabled}
                  />
                }
                autoComplete="off"
                value={dataSettings.text_color}
                onChange={handleChange('text_color')}
                disabled={isDisabled}
              />
              {!!dataSettings.enable_timeline_layout && (
                <TextField
                  label={'Timeline icon color'}
                  suffix={
                    <input
                      type="color"
                      defaultValue={dataSettings.icon_color}
                      key={dataSettings.icon_color}
                      onBlur={handleBlurColor('icon_color')}
                      disabled={isDisabled}
                    />
                  }
                  autoComplete="off"
                  value={dataSettings.icon_color}
                  onChange={handleChange('icon_color')}
                  disabled={isDisabled}
                />
              )}

              <div>
                <TextField
                  label={'Background color'}
                  suffix={
                    <InlineGrid columns={{ xl: 2, lg: 2, md: 2, sm: 2, xs: 2 }}>
                      <div style={{ display: 'flex', justifyContent: 'center' }}>
                        <Button
                          icon={dataSettings.background_color === 'transparent' ? HideIcon : ViewIcon}
                          accessibilityLabel="Add theme"
                          variant="plain"
                          textAlign="center"
                          onClick={() => {
                            handleChange('background_color')(
                              dataSettings.background_color === 'transparent' ? InitDataSettings.background_color : 'transparent',
                            );
                          }}
                          disabled={isDisabled}
                        />
                      </div>
                      <div style={{ alignContent: 'center', textAlign: 'center' }}>
                        <input
                          type="color"
                          defaultValue={
                            dataSettings.background_color === 'transparent'
                              ? InitDataSettings.background_color
                              : dataSettings.background_color
                          }
                          key={dataSettings.background_color}
                          onBlur={handleBlurColor('background_color')}
                          disabled={isDisabled}
                        />
                      </div>
                    </InlineGrid>
                  }
                  autoComplete="off"
                  value={dataSettings.background_color}
                  onChange={handleChange('background_color')}
                  disabled={isDisabled}
                />
              </div>

              <TextField
                label={'Border color'}
                suffix={
                  <InlineGrid columns={{ xl: 2, lg: 2, md: 2, sm: 2, xs: 2 }}>
                    <div style={{ display: 'flex', justifyContent: 'center' }}>
                      <Button
                        icon={dataSettings.border_color === 'transparent' ? HideIcon : ViewIcon}
                        accessibilityLabel="Add theme"
                        variant="plain"
                        textAlign="center"
                        onClick={() => {
                          handleChange('border_color')(
                            dataSettings.border_color === 'transparent' ? InitDataSettings.border_color : 'transparent',
                          );
                        }}
                        disabled={isDisabled}
                      />
                    </div>
                    <div style={{ alignContent: 'center', textAlign: 'center' }}>
                      <input
                        type="color"
                        defaultValue={
                          dataSettings.border_color === 'transparent' ? InitDataSettings.border_color : dataSettings.border_color
                        }
                        key={dataSettings.border_color}
                        onBlur={handleBlurColor('border_color')}
                        disabled={isDisabled}
                      />
                    </div>
                  </InlineGrid>
                }
                autoComplete="off"
                value={dataSettings.border_color}
                onChange={handleChange('border_color')}
                disabled={isDisabled}
              />

              <TextField
                label={'Text size'}
                autoComplete="off"
                type="number"
                min={1}
                max={48}
                value={dataSettings.text_size.toString()}
                onChange={(value: string) => handleChange('text_size')(Math.min(Math.max(1, Number(value)), 48))}
                disabled={isDisabled}
              />
            </InlineGrid>
          </div>
        </div>
      </CustomCard>
      <CustomCard title="Date appearance" textClassName="bold-text-styled" isHighlight={isHighlightedDateAppearance}>
        <div className="mt-8" ref={dateAppearanceRef}>
          <InlineGrid columns={{ xl: 2, lg: 2, md: 2, sm: 2, xs: 1 }} gap={'400'}>
            <Select
              options={dateFormatOptions}
              value={dataSettings.date_format}
              onChange={handleChange('date_format')}
              label={'Date format'}
            />
            <Select
              options={dateDisplayMode}
              value={dataSettings.date_display_mode}
              onChange={handleChange('date_display_mode')}
              label={'Date display mode'}
            />
          </InlineGrid>
          <p className="mt-8">
            Looking to change date language?{' '}
            <Link onClick={handleNavigateToTranslation} removeUnderline>
              Set default language
            </Link>
          </p>
        </div>
      </CustomCard>
      <CountdownSettings />
      <CustomCard
        title={<span className={`text--lg ${isPlanLimited ? 'text--gray' : ''}`}>Zipcode checker</span>}
        titleContentRight={
          !isMobile ? (
            <div className="min-w-fit">
              <UpgradeTextWithTouchPoint isVisible={isPlanLimited} text="Pro" />
            </div>
          ) : undefined
        }
      >
        <RegularText className={`${isPlanLimited ? 'text--gray' : ''}`}>For rules with zipcode condition</RegularText>

        <div>
          <UpgradeTextWithTouchPoint isVisible={isPlanLimited && isMobile} text="Pro" />
        </div>

        <div className="mt-8">
          <BoldSmallText className={`${isPlanLimited ? 'text--gray' : ''}`}>Input field</BoldSmallText>
        </div>
        <div className="mt-8" onClick={handleClickHighlight('isHighlightedZipcodeCheckerLabel')}>
          <TextField
            id={HighlightContentPreviewId.ZipcodeCheckerLabel}
            autoComplete="off"
            value={zipcodeSetting.inputLabel}
            onChange={handleChangeZipcodeSettings('inputLabel')}
            label={'Label'}
            disabled={isPlanLimited}
          />
        </div>
        <div
          ref={zipcodeCheckerInputIndicatorRef}
          className={`mt-16 ${isHighlightedZipcodeCheckerInputIndicatorSetting ? 'item-highlight-outline border-radius-8' : ''}`}
          onClick={handleClickHighlight('isHighlightedZipcodeCheckerInputIndicator')}
        >
          <TextField
            autoComplete="off"
            value={zipcodeSetting.inputPlaceholder || ''}
            onChange={handleChangeZipcodeSettings('inputPlaceholder')}
            label={'Input indicator'}
            maxLength={64}
            showCharacterCount
            disabled={isPlanLimited}
          />
        </div>

        <div className="mt-16">
          <Divider />
        </div>

        <div className="mt-16">
          <BoldSmallText className={`${isPlanLimited ? 'text--gray' : ''}`}>Validation message</BoldSmallText>
        </div>

        <div className="mt-16" onClick={handleClickHighlight('isHighlightedZipcodeCheckerValidText')}>
          <RegularText className={`${isPlanLimited ? 'text--gray' : ''}`}>Text if zipcode is valid</RegularText>
          <TextField
            id={HighlightContentPreviewId.ZipcodeValidText}
            autoComplete="off"
            value={zipcodeSetting.zipcodeAvailableText}
            onChange={handleChangeZipcodeSettings('zipcodeAvailableText')}
            label={'This will show above Message text'}
            disabled={isPlanLimited}
          />
        </div>
        <div className="mt-16" onClick={handleClickHighlight('isHighlightedZipcodeCheckerInvalidText')}>
          <TextField
            id={HighlightContentPreviewId.ZipcodeInvalidText}
            autoComplete="off"
            value={zipcodeSetting.zipcodeNotAvailableText}
            onChange={handleChangeZipcodeSettings('zipcodeNotAvailableText')}
            label={'Text if zipcode is invalid'}
            disabled={isPlanLimited}
          />
        </div>

        <div className="mt-16">
          <Divider />
        </div>

        <div
          className={`mt-16 ${isHighlightedZipcodeCheckerSubmitButtonSetting ? 'item-highlight-outline border-radius-8' : ''}`}
          onClick={handleClickHighlight('isHighlightedZipcodeCheckerSubmitButton')}
        >
          <BoldSmallText className={`${isPlanLimited ? 'text--gray' : ''}`}>Button</BoldSmallText>
          <div className="mt-16">
            <TextField
              autoComplete="off"
              value={zipcodeSetting.submitButton || ''}
              onChange={handleChangeZipcodeSettings('submitButton')}
              label={'Text'}
              maxLength={16}
              showCharacterCount
              disabled={isPlanLimited}
            />
          </div>
          <div className="mt-16">
            <InlineGrid columns={{ xl: 2, lg: 2, md: 2, sm: 2, xs: 1 }} gap={'400'}>
              <TextField
                autoComplete="off"
                value={zipcodeSetting.submitButtonTextColor}
                onChange={handleChangeZipcodeSettings('submitButtonTextColor')}
                label={'Text color'}
                suffix={
                  <input
                    type="color"
                    key={zipcodeSetting.submitButtonTextColor}
                    defaultValue={zipcodeSetting.submitButtonTextColor}
                    onBlur={handleBlurColorZipcodeSettings('submitButtonTextColor')}
                    disabled={isPlanLimited}
                  />
                }
                disabled={isPlanLimited}
              />
              <TextField
                autoComplete="off"
                value={zipcodeSetting.submitButtonBackgroundColor}
                onChange={handleChangeZipcodeSettings('submitButtonBackgroundColor')}
                label={'Background'}
                suffix={
                  <input
                    type="color"
                    key={zipcodeSetting.submitButtonBackgroundColor}
                    defaultValue={zipcodeSetting.submitButtonBackgroundColor}
                    onBlur={handleBlurColorZipcodeSettings('submitButtonBackgroundColor')}
                    disabled={isPlanLimited}
                  />
                }
                disabled={isPlanLimited}
              />
            </InlineGrid>
          </div>
        </div>
      </CustomCard>
      <CustomCss />
    </AppearanceStyled>
  );
};

export default memo(Appearance);
